import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Send, Instagram, Linkedin, Twitter, Facebook } from 'lucide-react';
import { siteData } from '../data/mock';
import { useToast } from '../hooks/use-toast';

export default function Footer() {
  const [email, setEmail] = useState('');
  const { toast } = useToast();

  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast({ title: 'Welcome to the network', description: `We'll keep ${email} in the loop.` });
    setEmail('');
  };

  return (
    <footer className="relative bg-[#03050c] border-t border-[#d5b56b]/10 overflow-hidden">
      <div className="absolute inset-0 grid-lines opacity-10" />
      {/* Top block — full-width layout */}
      <div className="relative z-10 pt-24 pb-12 w-full px-8 md:px-16 lg:px-24">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-12">
          {/* Newsletter */}
          <div className="lg:flex-1 lg:max-w-2xl">
            <div className="text-xs tracking-[0.4em] uppercase text-[#d5b56b] mb-6">
              — Join Our Network
            </div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] text-[#eef1f8]">
              Stay in the loop with
              <br />
              <span className="italic text-gold">insight, ideas & updates.</span>
            </h2>
            <form onSubmit={subscribe} className="mt-10 max-w-lg flex items-center gap-4 border-b border-[#d5b56b]/30 pb-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 bg-transparent outline-none text-[#eef1f8] placeholder:text-[#eef1f8]/40 py-2"
              />
              <button type="submit" className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#d5b56b] hover:text-[#eef1f8] transition-colors">
                Sign up <Send size={14} />
              </button>
            </form>
          </div>

          {/* Links */}
          <div className="flex gap-16 md:gap-24 lg:gap-32 shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#eef1f8]/40 mb-4">Navigate</div>
              <ul className="space-y-3">
                {siteData.navigation.map((n) => (
                  <li key={n.label}>
                    <a href={n.href} className="text-[#eef1f8]/70 hover:text-[#d5b56b] transition-colors text-sm">
                      {n.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#eef1f8]/40 mb-4">Connect</div>
              <ul className="space-y-3">
                {[
                  { Icon: Instagram, label: 'Instagram' },
                  { Icon: Linkedin, label: 'LinkedIn' },
                  { Icon: Twitter, label: 'Twitter' },
                  { Icon: Facebook, label: 'Facebook' },
                ].map(({ Icon, label }) => (
                  <li key={label}>
                    <a href="#" className="flex items-center gap-3 text-[#eef1f8]/70 hover:text-[#d5b56b] transition-colors text-sm group">
                      <Icon size={14} />
                      <span>{label}</span>
                      <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Massive brand mark — full viewport width, same source as navbar logo */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className="relative z-10 mt-10 w-full px-4"
      >
        {/* Thin gold rules that flank the wordmark */}
        <div className="flex items-center gap-6 md:gap-10">
          <span className="hidden md:block flex-1 h-px bg-gradient-to-r from-transparent via-[#d5b56b]/30 to-[#d5b56b]/50" />
          {/* Wordmark styled to match the navbar logo (BRETECH + SOLUTIONS GROUP) */}
          <div className="shrink-0 flex flex-col items-center leading-none" style={{ width: 'min(90vw, 1100px)' }}>
            {/* BRETECH */}
            <div
              className="flex justify-between w-full font-bold uppercase text-transparent bg-clip-text bg-gradient-to-b from-[#f5e0a7] via-[#d5b56b] to-[#8a6a2a] select-none"
              style={{
                fontFamily: "'Space Grotesk', 'Inter', sans-serif",
                fontSize: 'clamp(3.5rem, 13vw, 18rem)',
                letterSpacing: '0.03em',
                lineHeight: 0.9,
                paddingBottom: '0.15em',
              }}
            >
              {'BRETECH'.split('').map((c, i) => (
                <span key={`b-${i}`}>{c}</span>
              ))}
            </div>
            {/* SOLUTIONS GROUP */}
            <div
              className="flex justify-between w-full font-medium uppercase text-[#d5b56b]/75 select-none mt-2 md:mt-4"
              style={{
                fontFamily: "'Space Grotesk', 'Inter', sans-serif",
                fontSize: 'clamp(0.85rem, 2.4vw, 3.2rem)',
                letterSpacing: '0.28em',
                lineHeight: 1,
              }}
            >
              {'SOLUTIONS GROUP'.split('').map((c, i) => (
                <span key={`s-${i}`}>{c === ' ' ? '\u00A0' : c}</span>
              ))}
            </div>
          </div>
          <span className="hidden md:block flex-1 h-px bg-gradient-to-l from-transparent via-[#d5b56b]/30 to-[#d5b56b]/50" />
        </div>
      </motion.div>

      <div className="relative z-10 pb-12 w-full px-8 md:px-16 lg:px-24">
        <div className="mt-12 pt-6 border-t border-[#d5b56b]/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#eef1f8]/40">{siteData.company.copyright}</p>
          <p className="text-xs text-[#eef1f8]/40" data-testid="footer-designed-by">
            Designed by:{' '}
            <a
              href="https://bretechsolutions.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#d5b56b]/80 hover:text-[#d5b56b] transition-colors"
            >
              bretechsolutions.com
            </a>
          </p>
          <div className="flex items-center gap-6 text-xs text-[#eef1f8]/40">
            <a href="#" className="hover:text-[#d5b56b] transition-colors">Privacy</a>
            <a href="#" className="hover:text-[#d5b56b] transition-colors">Terms</a>
            <a href="#" className="hover:text-[#d5b56b] transition-colors">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
